import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

interface StepProps {
  number: string;
  title: string;
  description: string;
  index: number;
}

const Step = ({ number, title, description, index }: StepProps) => {
  return (
    <div className="relative group">
      {/* Connection Line */}
      {index < 3 && (
        <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 transform translate-x-4">
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
            <i className="ri-arrow-right-line text-cyan-400 text-xl"></i>
          </div>
        </div>
      )}

      <div className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 backdrop-blur-sm p-8 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:scale-105 group-hover:shadow-lg group-hover:shadow-cyan-500/10">
        {/* Glow Effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Step Number */}
        <div className="relative mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center group-hover:shadow-lg group-hover:shadow-cyan-500/25 transition-all duration-300">
            <span className="text-2xl font-bold text-white">{number}</span>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
            {title}
          </h3>
          <p className="text-gray-300 leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const Integration = () => {
  const { t } = useTranslation('common');

  const steps = [
    {
      number: t('integration.steps.0.number'),
      title: t('integration.steps.0.title'),
      description: t('integration.steps.0.description')
    },
    {
      number: t('integration.steps.1.number'),
      title: t('integration.steps.1.title'),
      description: t('integration.steps.1.description')
    },
    {
      number: t('integration.steps.2.number'),
      title: t('integration.steps.2.title'),
      description: t('integration.steps.2.description')
    },
    {
      number: t('integration.steps.3.number'),
      title: t('integration.steps.3.title'),
      description: t('integration.steps.3.description')
    }
  ];

  return (
    <section id="integration" className="py-20 bg-slate-800 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('integration.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('integration.subtitle')}
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <Step
              key={index}
              number={step.number}
              title={step.title}
              description={step.description}
              index={index}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex items-center space-x-4 p-8 bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-3xl border border-slate-600/50 backdrop-blur-sm">
            <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center">
              <i className="ri-book-open-line text-2xl text-white"></i>
            </div>
            <div className="text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                {t('integration.ready')}
              </h3>
              <p className="text-gray-300">
                {t('integration.readyDesc')}
              </p>
            </div>
            <a
              href="/docs/en/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center font-medium transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 px-8 py-4 text-lg rounded-xl"
            >
              {t('integration.cta')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Integration;